/**
 * CommunityPage — WhatsApp-style two-panel group chat.
 *
 * Left panel  : community list + search
 * Right panel : chat header, message list, send bar
 *
 * On mobile (<768 px) the left panel is full-screen; tapping a community
 * slides in the chat panel.
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import { CommunityComposerBar } from '../components/community/CommunityComposerBar';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useCommunity, OptimisticMessage } from '../contexts/CommunityContext';
import { useAuth } from '../contexts/AuthContext';
import { Community } from '../services/communityService';
import { CommunityInfoDrawer } from '../components/community/CommunityInfoDrawer';
import { EditCommunityModal } from '../components/community/EditCommunityModal';
import { CommunityMessageBubble } from '../components/community/CommunityMessageBubble';
import { MessageSelectionBar } from '../components/community/MessageSelectionBar';
import { MessageActionMenu } from '../components/community/MessageActionMenu';
import { ForwardCommunityPicker } from '../components/community/ForwardCommunityPicker';
import { PinnedMessageBanner } from '../components/community/PinnedMessageBanner';
import { DeleteMessagesDialog } from '../components/community/DeleteMessagesDialog';
import { avatarColor, getInitials } from '../components/community/chatHelpers';
import { useMessageSelection } from '../hooks/useMessageSelection';
import {
  getSelectedMessages,
  canShowCopy,
  canShowDelete,
  canShowDownload,
  canShowPin,
  canShowReply,
  buildCopyText,
} from '../utils/messageSelectionUtils';
import { downloadMessageMedia } from '../utils/downloadMessageMedia';
import {
  canEditCommunity,
  mustTransferBeforeLeave,
  isOnlyCommunityMember,
  type CommunityRole,
} from '../utils/communityPermissions';
import { getCommunityErrorCode, getCommunityErrorMessage } from '../utils/communityApiErrors';
import { resolveClientAppSenderDisplayName, COMMUNITY_SUPPORT_TEAM_LABEL } from '../utils/communitySenderDisplay';
import { TransferOwnershipModal } from '../components/community/TransferOwnershipModal';
import { CommunityConfirmDialog } from '../components/community/CommunityConfirmDialog';
import { MessagingThreadList } from '../components/communication/MessagingThreadList';
import { useChat } from '../contexts/ChatContext';
import { useSidebar } from '../contexts/SidebarContext';
import { Thread } from '../services/chatService';
import { CWY_COLORS } from '../theme/colors';

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const CommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { communityId } = useParams<{ communityId?: string }>();
  const {
    communities,
    loadingCommunities,
    selectedCommunity,
    activeGroup,
    messages,
    loadingMessages,
    hasMoreMessages,
    pinnedMessage,
    typingUsers,
    isSocketConnected,
    unreadMap,
    selectCommunity,
    clearCommunitySelection,
    deleteMessages,
    forwardMessages,
    pinMessage,
    setReplyTo,
    loadMoreMessages,
    leaveCommunity,
    transferOwnership,
    loadMembers,
    members,
  } = useCommunity();
  const { client } = useAuth();
  const {
    threads,
    setSelectedThread,
    loadingThreads,
  } = useChat();
  const { isMobile } = useSidebar();

  const selectionClearKey = selectedCommunity?.community_id ?? null;
  const {
    selectedIds,
    isSelectionMode,
    count: selectionCount,
    clearSelection,
    toggleMessage,
    isSelected,
  } = useMessageSelection(selectionClearKey);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [leaveSnapshot, setLeaveSnapshot] = useState<{
    community_id: string;
    name: string;
    my_role?: CommunityRole;
    member_count: number;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const selectedMessages = getSelectedMessages(messages, selectedIds);
  const messageById = useMemo(
    () => new Map(messages.map((m) => [m.community_message_id, m])),
    [messages]
  );

  const showCopy = canShowCopy(selectedMessages);
  const showDelete = canShowDelete(selectedMessages, client?.client_id);
  const showDownload = canShowDownload(selectedMessages);
  const showReply = canShowReply(selectedMessages);
  const showPin = canShowPin(selectedMessages);
  const pinLabel =
    selectedMessages.length === 1 && selectedMessages[0].is_pinned ? 'Unpin' : 'Pin';

  const memberRoleByClientId = useMemo(() => {
    const map = new Map<string, CommunityRole>();
    for (const m of members) {
      map.set(m.client_id, m.role);
    }
    if (client?.client_id && selectedCommunity?.my_role) {
      map.set(client.client_id, selectedCommunity.my_role);
    }
    return map;
  }, [members, client?.client_id, selectedCommunity?.my_role]);

  const memberIsStaffByClientId = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const m of members) {
      if (m.is_staff) map.set(m.client_id, true);
    }
    return map;
  }, [members]);

  const memberNameByClientId = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      map.set(m.client_id, m.display_name?.trim() || m.client_id);
    }
    if (client?.client_id) {
      map.set(client.client_id, client.name?.trim() || client.client_id);
    }
    return map;
  }, [members, client?.client_id, client?.name]);

  const resolveSenderDisplayName = useCallback(
    (senderClientId?: string, cachedName?: string) =>
      resolveClientAppSenderDisplayName(
        senderClientId,
        cachedName,
        memberNameByClientId,
        memberRoleByClientId,
        memberIsStaffByClientId
      ),
    [memberNameByClientId, memberRoleByClientId, memberIsStaffByClientId]
  );

  // Auto-select community after invite join redirect
  useEffect(() => {
    const autoId = (location.state as { autoSelectCommunityId?: string } | null)
      ?.autoSelectCommunityId;
    if (!autoId || selectedCommunity?.community_id === autoId) return;
    const found = communities.find((c) => c.community_id === autoId);
    if (found) {
      navigate(`/community/${autoId}`, { replace: true, state: {} });
      void selectCommunity(found);
    }
  }, [
    communities,
    location.state,
    selectedCommunity?.community_id,
    selectCommunity,
    navigate,
  ]);

  // Sync URL community id → open chat
  useEffect(() => {
    if (!communityId) {
      if (selectedCommunity || activeGroup) {
        clearCommunitySelection();
      }
      return;
    }

    const found = communities.find((c) => c.community_id === communityId);
    if (!found) {
      if (!loadingCommunities) {
        navigate('/chat', { replace: true });
      }
      return;
    }

    if (selectedCommunity?.community_id === communityId && activeGroup) return;
    if (selectedCommunity?.community_id === communityId && loadingMessages) {
      return;
    }

    void selectCommunity(found).catch(() => {
      navigate('/chat', { replace: true });
    });
  }, [
    communityId,
    communities,
    loadingCommunities,
    selectedCommunity,
    activeGroup,
    loadingMessages,
    selectCommunity,
    clearCommunitySelection,
    navigate,
  ]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!loadingMessages && messages.length) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, loadingMessages]);

  // Preserve scroll position when older messages are prepended
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const diff = container.scrollHeight - prevScrollHeightRef.current;
    if (diff > 0 && prevScrollHeightRef.current > 0) {
      container.scrollTop += diff;
    }
    prevScrollHeightRef.current = container.scrollHeight;
  }, [messages]);

  // IntersectionObserver for infinite scroll (scroll up triggers loadMore)
  useEffect(() => {
    const sentinel = messagesTopRef.current;
    if (!sentinel || !hasMoreMessages) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          prevScrollHeightRef.current = scrollContainerRef.current?.scrollHeight ?? 0;
          loadMoreMessages();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreMessages, loadMoreMessages]);

  const handleSelectCommunity = useCallback(
    async (c: Community) => {
      navigate(`/community/${c.community_id}`);
      try {
        await selectCommunity(c);
      } catch {
        navigate('/chat', { replace: true });
      }
    },
    [navigate, selectCommunity]
  );

  const handleSelectSupportThread = useCallback(
    (thread: Thread) => {
      clearCommunitySelection();
      setSelectedThread(thread);
      navigate('/chat');
    },
    [clearCommunitySelection, setSelectedThread, navigate]
  );

  const showingChat = Boolean(selectedCommunity && activeGroup);
  const hasListItems = threads.length > 0 || communities.length > 0;
  const showListPanel = !isMobile || !showingChat;
  const showMainPanel = !isMobile || showingChat || !hasListItems;

  const messagingShellSx = {
    flex: 1,
    display: 'flex',
    height: '100%',
    minHeight: 0,
    border: `1px solid ${CWY_COLORS.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    bgcolor: '#fff',
  } as const;

  const handleBackToHub = useCallback(() => {
    clearCommunitySelection();
    navigate('/chat');
  }, [clearCommunitySelection, navigate]);

  const handleLeave = () => {
    setMenuAnchor(null);
    setInfoDrawerOpen(false);
    if (!selectedCommunity) return;

    if (mustTransferBeforeLeave(selectedCommunity.my_role, selectedCommunity.member_count)) {
      void (async () => {
        if (!members.length) {
          await loadMembers(selectedCommunity.community_id);
        }
        setTransferOpen(true);
      })();
      return;
    }

    setLeaveSnapshot({
      community_id: selectedCommunity.community_id,
      name: selectedCommunity.name,
      my_role: selectedCommunity.my_role,
      member_count: selectedCommunity.member_count,
    });
    setLeaveConfirmOpen(true);
  };

  const handleAfterLeave = () => {
    setInfoDrawerOpen(false);
    setMenuAnchor(null);
    setLeaveConfirmOpen(false);
    setLeaveSnapshot(null);
  };

  const handleConfirmLeave = async () => {
    if (!leaveSnapshot) return;
    setLeaveLoading(true);
    try {
      await leaveCommunity(leaveSnapshot.community_id);
      handleAfterLeave();
    } catch (err: unknown) {
      if (getCommunityErrorCode(err) === 'OWNER_MUST_TRANSFER') {
        setLeaveConfirmOpen(false);
        setLeaveSnapshot(null);
        if (!members.length && selectedCommunity) {
          await loadMembers(selectedCommunity.community_id);
        }
        setTransferOpen(true);
      } else {
        setSnackbar(getCommunityErrorMessage(err, 'Failed to leave community'));
      }
    } finally {
      setLeaveLoading(false);
    }
  };

  const handleTransferConfirm = async (newOwnerClientId: string) => {
    if (!selectedCommunity) return;
    setLeaveLoading(true);
    try {
      await transferOwnership(selectedCommunity.community_id, newOwnerClientId);
      setSnackbar('Ownership transferred — you can leave now if you wish');
      setTransferOpen(false);
    } catch (err: unknown) {
      setSnackbar(getCommunityErrorMessage(err, 'Failed to transfer ownership'));
    } finally {
      setLeaveLoading(false);
    }
  };

  const closeActionMenu = () => setActionMenuAnchor(null);

  const handleCopySelected = async () => {
    closeActionMenu();
    const text = buildCopyText(selectedMessages);
    if (!text) {
      setSnackbar('Nothing to copy');
      return;
    }
    await navigator.clipboard.writeText(text);
    setSnackbar('Copied to clipboard');
    clearSelection();
  };

  const handleDownloadSelected = async () => {
    closeActionMenu();
    const withMedia = selectedMessages.filter((m) => m.media?.blob_url);
    try {
      for (const m of withMedia) {
        if (m.media) await downloadMessageMedia(m.media, m.media.filename || 'download');
      }
      setSnackbar(withMedia.length > 1 ? 'Downloads started' : 'Saved to downloads');
      clearSelection();
    } catch {
      setSnackbar('Download failed');
    }
  };

  const handleReplySelected = () => {
    closeActionMenu();
    if (selectedMessages.length !== 1) return;
    setReplyTo(selectedMessages[0]);
    clearSelection();
  };

  const handlePinSelected = async () => {
    closeActionMenu();
    if (selectedMessages.length !== 1) return;
    setActionLoading(true);
    try {
      await pinMessage(selectedMessages[0].community_message_id);
      setSnackbar(pinLabel === 'Unpin' ? 'Message unpinned' : 'Message pinned');
      clearSelection();
    } catch (err: any) {
      setSnackbar(err?.response?.data?.error?.message || 'Failed to pin message');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setActionLoading(true);
    try {
      await deleteMessages(selectedMessages.map((m) => m.community_message_id));
      setSnackbar('Message deleted for everyone');
      setDeleteOpen(false);
      clearSelection();
    } catch (err: any) {
      setSnackbar(err?.response?.data?.error?.message || 'Failed to delete message');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForwardToCommunity = async (targetCommunityId: string) => {
    setActionLoading(true);
    try {
      await forwardMessages(
        selectedMessages.map((m) => m.community_message_id),
        targetCommunityId
      );
      setForwardOpen(false);
      clearSelection();
      setSnackbar('Message forwarded');
    } catch (err: any) {
      setSnackbar(err?.response?.data?.error?.message || 'Failed to forward message');
    } finally {
      setActionLoading(false);
    }
  };

  const scrollToMessage = useCallback(
    async (messageId: string) => {
      const applyHighlight = () => {
        setHighlightedMessageId(messageId);
        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightedMessageId(null), 2200);
      };

      const tryScroll = (): boolean => {
        const el = messageRefs.current.get(messageId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          applyHighlight();
          return true;
        }
        return false;
      };

      if (tryScroll()) return;

      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      if (tryScroll()) return;

      for (let attempt = 0; attempt < 12; attempt++) {
        if (!hasMoreMessages) break;
        await loadMoreMessages();
        await new Promise<void>((resolve) => setTimeout(resolve, 150));
        if (tryScroll()) return;
      }

      setSnackbar('Could not find the original message');
    },
    [hasMoreMessages, loadMoreMessages]
  );

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const showEditCommunity = selectedCommunity && canEditCommunity(selectedCommunity.my_role);

  // Determine posting permission from active group
  const canPost = Boolean(
    activeGroup &&
      (activeGroup.post_policy === 'all_members' ||
        activeGroup.type !== 'announcement' ||
        // owner/admin can always post
        ['owner', 'admin'].includes(activeGroup.my_membership?.role ?? ''))
  );

  return (
    <Box sx={messagingShellSx}>
      {showListPanel && (
        <MessagingThreadList
          threads={threads}
          selectedThread={null}
          loadingThreads={loadingThreads}
          onSelectThread={handleSelectSupportThread}
          communities={communities}
          loadingCommunities={loadingCommunities}
          selectedCommunityId={selectedCommunity?.community_id ?? null}
          unreadMap={unreadMap}
          onSelectCommunity={handleSelectCommunity}
        />
      )}

      {showMainPanel && (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#f5f5f5', minWidth: 0 }}>
          {selectedCommunity && activeGroup ? (
            <>
              {/* Chat header */}
              {isSelectionMode ? (
                <MessageSelectionBar
                  count={selectionCount}
                  onCancel={clearSelection}
                  onForward={() => setForwardOpen(true)}
                  onOpenMenu={setActionMenuAnchor}
                />
              ) : (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: '#fff',
                  borderBottom: '1px solid #e8eaed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleBackToHub}
                  sx={{ color: '#606060', mr: -0.5, display: { xs: 'inline-flex', md: 'none' } }}
                  aria-label="Back to messages"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Avatar
                  src={selectedCommunity.avatar_url}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: avatarColor(selectedCommunity.community_id),
                    cursor: 'pointer',
                  }}
                  onClick={() => setInfoDrawerOpen(true)}
                >
                  {getInitials(selectedCommunity.name)}
                </Avatar>
                <Box
                  sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  onClick={() => setInfoDrawerOpen(true)}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedCommunity.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#9aa0a6' }}>
                    {selectedCommunity.member_count} member
                    {selectedCommunity.member_count !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                {!isSocketConnected && (
                  <Tooltip title="Reconnecting…">
                    <WifiOffIcon sx={{ color: '#f57c00', fontSize: 20 }} />
                  </Tooltip>
                )}

                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ color: '#606060' }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  PaperProps={{ sx: { borderRadius: 2 } }}
                >
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setInfoDrawerOpen(true);
                    }}
                    sx={{ fontSize: '0.88rem' }}
                  >
                    Community info
                  </MenuItem>
                  {showEditCommunity && (
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        setEditOpen(true);
                      }}
                      sx={{ fontSize: '0.88rem' }}
                    >
                      Edit community
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={handleLeave}
                    disabled={leaveLoading}
                    sx={{ color: '#d32f2f', fontSize: '0.88rem' }}
                  >
                    Leave Community
                  </MenuItem>
                </Menu>
              </Box>
              )}

              <MessageActionMenu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={closeActionMenu}
                showForward
                showCopy={showCopy}
                showDownload={showDownload}
                showReply={showReply}
                showPin={showPin}
                pinLabel={pinLabel}
                showDelete={showDelete}
                onForward={() => {
                  closeActionMenu();
                  setForwardOpen(true);
                }}
                onCopy={handleCopySelected}
                onDownload={handleDownloadSelected}
                onReply={handleReplySelected}
                onPin={handlePinSelected}
                onDelete={() => {
                  closeActionMenu();
                  setDeleteOpen(true);
                }}
              />

              <ForwardCommunityPicker
                open={forwardOpen}
                communities={communities}
                currentCommunityId={selectedCommunity.community_id}
                loading={actionLoading}
                onClose={() => setForwardOpen(false)}
                onSelect={handleForwardToCommunity}
              />

              <DeleteMessagesDialog
                open={deleteOpen}
                count={selectionCount}
                loading={actionLoading}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
              />

              {/* Messages area */}
              <Box
                ref={scrollContainerRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Sentinel for infinite scroll */}
                <div ref={messagesTopRef} />

                {loadingMessages && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ color: '#9aa0a6' }} />
                  </Box>
                )}

                {!loadingMessages && messages.length === 0 && (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#9aa0a6', fontSize: '0.88rem' }}>
                      No messages yet. Say hello! 👋
                    </Typography>
                  </Box>
                )}

                {pinnedMessage && !isSelectionMode && (
                  <PinnedMessageBanner
                    message={pinnedMessage}
                    onScrollTo={() => scrollToMessage(pinnedMessage.community_message_id)}
                    onUnpin={() => pinMessage(pinnedMessage.community_message_id).catch(() => setSnackbar('Failed to unpin'))}
                  />
                )}

                {messages.map((msg) => {
                  const isOwn = Boolean(
                    msg.sender_client_id === client?.client_id || msg._optimistic
                  );
                  const isDeleted = msg.message_type === 'deleted' || msg.deleted;
                  const senderDisplayName = resolveSenderDisplayName(
                    msg.sender_client_id,
                    msg.sender_name_cached
                  );
                  const showSender =
                    msg.message_type !== 'system_event' &&
                    !isDeleted &&
                    Boolean(msg.sender_client_id || msg.sender_name_cached);
                  const senderRole =
                    senderDisplayName === COMMUNITY_SUPPORT_TEAM_LABEL
                      ? undefined
                      : msg.sender_client_id
                        ? memberRoleByClientId.get(msg.sender_client_id)
                        : isOwn
                          ? selectedCommunity?.my_role
                          : undefined;
                  const replyParent = msg.reply_to_message_id
                    ? messageById.get(msg.reply_to_message_id) ??
                      (msg.reply_preview
                        ? {
                            community_message_id: msg.reply_preview.community_message_id,
                            sender_client_id: undefined,
                            sender_name_cached: msg.reply_preview.sender_name,
                            content: msg.reply_preview.content_snippet,
                            message_type: msg.reply_preview.message_type,
                            deleted: msg.reply_preview.message_type === 'deleted',
                          }
                        : null)
                    : null;
                  const replyParentResolved = replyParent
                    ? {
                        ...replyParent,
                        sender_name_cached: resolveSenderDisplayName(
                          'sender_client_id' in replyParent
                            ? replyParent.sender_client_id
                            : undefined,
                          replyParent.sender_name_cached
                        ),
                      }
                    : null;
                  return (
                    <Box
                      key={msg.community_message_id}
                      ref={(el: HTMLDivElement | null) => {
                        if (el) messageRefs.current.set(msg.community_message_id, el);
                        else messageRefs.current.delete(msg.community_message_id);
                      }}
                      sx={{
                        borderRadius: 1,
                        ...(highlightedMessageId === msg.community_message_id && {
                          animation: 'replyTargetPulse 2.2s ease-out',
                          '@keyframes replyTargetPulse': {
                            '0%': {
                              backgroundColor: 'rgba(15, 157, 88, 0.32)',
                              boxShadow: '0 0 0 0 rgba(15, 157, 88, 0.45)',
                            },
                            '35%': {
                              backgroundColor: 'rgba(15, 157, 88, 0.18)',
                              boxShadow: '0 0 16px 4px rgba(15, 157, 88, 0.28)',
                            },
                            '100%': {
                              backgroundColor: 'transparent',
                              boxShadow: 'none',
                            },
                          },
                        }),
                      }}
                    >
                      <CommunityMessageBubble
                        message={msg}
                        isOwn={isOwn}
                        showSender={showSender}
                        senderDisplayName={senderDisplayName}
                        senderRole={senderRole}
                        isSelected={isSelected(msg.community_message_id)}
                        onClick={() => toggleMessage(msg)}
                        onReplyPreviewClick={scrollToMessage}
                        replyToMessage={replyParentResolved as OptimisticMessage | null}
                      />
                    </Box>
                  );
                })}

                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <Box sx={{ px: 3, py: 0.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#9aa0a6', fontStyle: 'italic' }}>
                      {typingUsers.slice(0, 2).join(', ')}
                      {typingUsers.length > 2 ? ` and ${typingUsers.length - 2} others` : ''} typing…
                    </Typography>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>

              <CommunityComposerBar
                canPost={canPost}
                onNotify={setSnackbar}
              />
            </>
          ) : communityId && loadingMessages && !activeGroup ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={32} sx={{ color: '#8696a0' }} />
              <Typography variant="body2" sx={{ color: '#667781' }}>
                Opening community…
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 3,
              }}
            >
              <Typography variant="body2" sx={{ color: '#667781', textAlign: 'center' }}>
                Select a conversation from the list, or open Messaging to view your chats.
              </Typography>
            </Box>
          )}
      </Box>
      )}

      <CommunityConfirmDialog
        open={leaveConfirmOpen}
        title={
          leaveSnapshot?.my_role === 'owner' &&
          isOnlyCommunityMember(leaveSnapshot.member_count)
            ? 'Leave and delete community?'
            : 'Leave community?'
        }
        description={
          leaveSnapshot?.my_role === 'owner' &&
          isOnlyCommunityMember(leaveSnapshot.member_count)
            ? `You are the only member of "${leaveSnapshot.name}". Leaving will delete this community for everyone.`
            : `Leave "${leaveSnapshot?.name ?? 'this community'}"? You won't see new messages unless you rejoin.`
        }
        confirmLabel="Leave"
        confirmColor="error"
        loading={leaveLoading}
        onConfirm={handleConfirmLeave}
        onCancel={() => {
          if (leaveLoading) return;
          setLeaveConfirmOpen(false);
          setLeaveSnapshot(null);
        }}
      />

      {selectedCommunity && client && (
        <>
          <CommunityInfoDrawer
            open={infoDrawerOpen}
            community={selectedCommunity}
            actorClientId={client.client_id}
            onClose={() => setInfoDrawerOpen(false)}
            onAfterLeave={handleAfterLeave}
            onNotify={setSnackbar}
          />
          <EditCommunityModal
            open={editOpen}
            community={selectedCommunity}
            onClose={() => setEditOpen(false)}
            onSaved={() => setSnackbar('Community updated')}
          />
          <TransferOwnershipModal
            open={transferOpen}
            communityName={selectedCommunity.name}
            members={members}
            actorClientId={client.client_id}
            loading={leaveLoading}
            onConfirm={handleTransferConfirm}
            onClose={() => !leaveLoading && setTransferOpen(false)}
          />
        </>
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
